import { useMemo } from "react";
import { Badge, Button, Flex, Text } from "@radix-ui/themes";
import { Info, ThumbsUp } from "lucide-react";

import { RegressionTestResultDetail } from "@/v2/entities/regression/Regression.model";
import { getSimilarityColor, filterActionMessages } from "./utils";
import styles from "./styles.module.scss";

interface ResultDetailViewProps {
  result: RegressionTestResultDetail;
  onForget: () => void;
  isForgetLoading: boolean;
  onRemoveFromSet: () => void;
  isRemovingFromSet: boolean;
}

export function ResultDetailView({
  result,
  onForget,
  isForgetLoading,
  onRemoveFromSet,
  isRemovingFromSet,
}: ResultDetailViewProps) {
  const isCustomQuestion = !result.regressionSetItem.releaseConversation;

  const allSessionComments = useMemo(() => {
    const raw =
      result.regressionSetItem?.releaseConversation?.releaseComments ?? [];
    return filterActionMessages(raw);
  }, [result.regressionSetItem?.releaseConversation?.releaseComments]);

  const sessionComments = useMemo(() => {
    if (
      result.regressionSetItem.expectedDecision === "escalate" &&
      allSessionComments.length > 0
    ) {
      const lastAgentIndex = allSessionComments
        .map((c, i) => (c.isAgent ? i : -1))
        .filter((i) => i !== -1)
        .pop();
      if (lastAgentIndex !== undefined && lastAgentIndex !== -1) {
        return allSessionComments.filter((_, i) => i !== lastAgentIndex);
      }
    }
    return allSessionComments;
  }, [allSessionComments, result.regressionSetItem.expectedDecision]);

  const regressionComments = useMemo(() => {
    if (isCustomQuestion || allSessionComments.length === 0) return [];

    let lastIndex = allSessionComments.length - 1;
    while (lastIndex >= 0 && allSessionComments[lastIndex].isAgent) {
      lastIndex--;
    }
    return allSessionComments.slice(0, lastIndex + 1);
  }, [allSessionComments, isCustomQuestion]);

  if (!result.regressionSetItem) {
    return (
      <div className={styles.emptyState}>
        <Text size="3" color="gray">
          This item has been removed
        </Text>
      </div>
    );
  }

  const firstMessage = isCustomQuestion
    ? result.regressionSetItem.question
    : sessionComments[0]?.comment ||
      result.regressionSetItem.question ||
      "No question";

  return (
    <div className={styles.resultDetailContainer}>
      <div className={styles.resultDetailHeader}>
        <Text size="2" className={styles.questionTitle}>
          {firstMessage}
        </Text>
        <Badge size="2" color={getSimilarityColor(result.similarity)}>
          {Math.round(result.similarity * 100)}% Similarity
        </Badge>
      </div>

      <div className={styles.comparisonContainer}>
        <div className={styles.comparisonColumn}>
          <div className={styles.columnHeader}>
            <Badge size="1" color="green">
              <span className={styles.badgeLabel}>
                {isCustomQuestion ? "Expected" : "Session"}
                {!isCustomQuestion && (
                  <ThumbsUp size={16} className={styles.badgeIcon} />
                )}
              </span>
            </Badge>
            <Flex gap="2" align="center">
              <Button
                size="1"
                variant="outline"
                color="gray"
                onClick={onRemoveFromSet}
                disabled={isRemovingFromSet}
              >
                {isRemovingFromSet ? "Removing..." : "Remove from set"}
              </Button>
              {!isCustomQuestion && (
                <Button
                  size="1"
                  variant="outline"
                  color="red"
                  onClick={onForget}
                  disabled={
                    isForgetLoading ||
                    !result.regressionSetItem?.releaseConversation?.id
                  }
                >
                  {isForgetLoading ? "Forgetting..." : "Forget"}
                </Button>
              )}
            </Flex>
          </div>
          <div className={styles.messageContainer}>
            {isCustomQuestion ? (
              <>
                <div className={styles.userMessage}>
                  <Text size="2">{result.regressionSetItem.question}</Text>
                </div>
                {result.regressionSetItem.expectedAnswer &&
                  result.regressionSetItem.expectedDecision !== "escalate" && (
                    <div className={styles.agentMessage}>
                      <Text size="2">
                        {result.regressionSetItem.expectedAnswer}
                      </Text>
                    </div>
                  )}
              </>
            ) : sessionComments.length > 0 ? (
              sessionComments.map((comment) => (
                <div
                  key={comment.id}
                  className={
                    comment.isAgent ? styles.agentMessage : styles.userMessage
                  }
                >
                  <Text size="2">{comment.comment}</Text>
                </div>
              ))
            ) : (
              <Text size="2" color="gray">
                No conversation available
              </Text>
            )}
            {result.regressionSetItem.expectedDecision === "escalate" && (
              <div className={styles.escalationMessage}>
                <Info size={16} />
                <Text size="2">Quack will escalate it to an agent</Text>
              </div>
            )}
          </div>
        </div>

        <div className={styles.comparisonColumn}>
          <div className={styles.columnHeader}>
            <Badge size="1" color="blue">
              Regression result
            </Badge>
          </div>
          <div className={styles.messageContainer}>
            {isCustomQuestion ? (
              <>
                <div className={styles.userMessage}>
                  <Text size="2">{result.regressionSetItem.question}</Text>
                </div>
                {result.aiResponse && result.decision !== "escalate" && (
                  <div className={styles.agentMessage}>
                    <Text size="2">{result.aiResponse}</Text>
                  </div>
                )}
              </>
            ) : (
              <>
                {regressionComments.map((comment) => (
                  <div
                    key={comment.id}
                    className={
                      comment.isAgent ? styles.agentMessage : styles.userMessage
                    }
                  >
                    <Text size="2">{comment.comment}</Text>
                  </div>
                ))}
                {result.aiResponse && result.decision !== "escalate" && (
                  <div className={styles.agentMessage}>
                    <Text size="2">{result.aiResponse}</Text>
                  </div>
                )}
              </>
            )}
            {result.decision === "escalate" && (
              <div className={styles.escalationMessage}>
                <Info size={16} />
                <Text size="2">Quack will escalate it to an agent</Text>
              </div>
            )}
            {!isCustomQuestion &&
              regressionComments.length === 0 &&
              !result.aiResponse &&
              result.decision !== "escalate" && (
                <Text size="2" color="gray">
                  No response available
                </Text>
              )}
          </div>
        </div>
      </div>
    </div>
  );
}
